import jwtDecode from 'jwt-decode';

import { useTokenStore } from './useTokenStore';
// Types
export type IJwtPayload = { exp?: number };

type IExpiryCache = {
  access: string;
  secondsLeft: number;
  measuredAt: number; // Date.now()
};

// Constants
const MS_IN_SECOND = 1_000;
const SECONDS_IN_MINUTE = 60;
const CACHE_TTL_MS = 1_000;

// State
let cache: IExpiryCache | null = null;

// Helpers
const clampZero = (value: number): number => Math.max(0, value);

const getNowSeconds = (): number => Math.floor(Date.now() / MS_IN_SECOND);

const safeDecodeJwt = (accessToken: string): IJwtPayload | null => {
  try {
    return jwtDecode<IJwtPayload>(accessToken);
  } catch {
    return null;
  }
};

const getSecondsLeftFromToken = (accessToken: string): number => {
  if (!accessToken) return 0;

  const decoded = safeDecodeJwt(accessToken);
  if (!decoded?.exp) return 0;

  return clampZero(decoded.exp - getNowSeconds());
};

export const tokenExpiry = {
  // -------------
  // Utils (JWT)
  // -------------
  getSecondsLeftFromJwt: (accessToken: string): number => getSecondsLeftFromToken(accessToken),

  getMinutesLeftFromJwt: (accessToken: string): number => {
    const secondsLeft = tokenExpiry.getSecondsLeftFromJwt(accessToken);
    return Math.ceil(secondsLeft / SECONDS_IN_MINUTE);
  },

  isJwtExpired: (accessToken: string): boolean =>
    tokenExpiry.getSecondsLeftFromJwt(accessToken) <= 0,

  // -------------------------
  // Cache (tokenStore access)
  // -------------------------
  getSecondsLeftCached: (): number => {
    const access = useTokenStore.getAccess();
    if (!access) return 0;

    const now = Date.now();

    if (cache && cache.access === access && now - cache.measuredAt <= CACHE_TTL_MS) {
      const elapsedSec = Math.floor((now - cache.measuredAt) / MS_IN_SECOND);
      return clampZero(cache.secondsLeft - elapsedSec);
    }

    const secondsLeft = getSecondsLeftFromToken(access);

    cache = {
      access,
      secondsLeft,
      measuredAt: now,
    };

    return secondsLeft;
  },

  clearCache: (): void => {
    cache = null;
  },

};

export function getSecondsLeftFromJwt(accessToken: string): number {
  return tokenExpiry.getSecondsLeftFromJwt(accessToken);
}

export function getMinutesLeftFromJwt(accessToken: string): number {
  return tokenExpiry.getMinutesLeftFromJwt(accessToken);
}

export function isJwtExpired(accessToken: string): boolean {
  return tokenExpiry.isJwtExpired(accessToken);
}

export function getSecondsLeftCached(): number {
  return tokenExpiry.getSecondsLeftCached();
}

export function clearExpiryCache(): void {
  tokenExpiry.clearCache();
}
